import gql from "graphql-tag";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import type {CooldownStatus} from "@repo/guide-types";

import type {getCooldownStatus_user_cooldownStatus as GQLCooldownStatus} from "./__graphql-types__/getCooldownStatus.ts";
import type {getCooldownStatusType} from "./__graphql-types__/index.ts";

export const USE_COOLDOWN_STATUS_QUERY: GqlOperation<
    getCooldownStatusType["response"],
    getCooldownStatusType["variables"]
> = gqlOp(gql`
    query getCooldownStatus {
        user {
            id
            hasCooldown
            cooldownStatus {
                hasAntiCheatCooldown
                lastAntiCheatCooldownExpiresAt
                hasTeacherDirectedCooldown
                teacherDirectedCooldownExpiresAt
                teacherDirectedCooldownStartedBy {
                    id
                    kaid
                    nickname
                }
            }
        }
    }
`);

export interface CooldownStatusResponse {
    status: string;
    hasCooldown: boolean;
    cooldownStatus: CooldownStatus;
    refetch: () => unknown;
}

interface Props {
    skip: boolean;
}

export const useFetchCooldownStatus = (
    props: Props,
): CooldownStatusResponse => {
    const {skip = false} = props;

    const [result, refetch] = useQuery(USE_COOLDOWN_STATUS_QUERY, {
        fetchPolicy: FetchPolicy.NetworkOnly,
        skip: skip,
    });

    switch (result.status) {
        case "loading":
        case "error":
        case "aborted":
        case "no-data":
            return {
                status: result.status,
                hasCooldown: false,
                cooldownStatus: {
                    hasCooldown: false,
                    cooldownType: "None",
                    expiresAt: null,
                    teacherKaid: null,
                    teacherName: null,
                },
                refetch: refetch,
            };
        case "success":
            const hasCooldown = result.data.user?.hasCooldown ?? false;
            const cooldownStatus =
                result.data.user?.cooldownStatus ?? undefined;
            return {
                status: result.status,
                hasCooldown: hasCooldown,
                // In practice, we don't expect to reach a scenario where
                // hasCooldown is true but cooldownStatus is undefined but we
                // will add a fallback just in case.
                cooldownStatus: mapCooldownStatus(cooldownStatus) ?? {
                    hasCooldown,
                    cooldownType: "None",
                    expiresAt: null,
                    teacherKaid: null,
                    teacherName: null,
                },
                refetch: refetch,
            };
    }
};

function mapCooldownStatus(
    status: GQLCooldownStatus | undefined,
): CooldownStatus | null {
    if (!status) {
        return null;
    }

    if (status.hasTeacherDirectedCooldown) {
        // Teacher-directed cooldowns take precedence over anti-cheat cooldowns.
        return {
            hasCooldown: true,
            cooldownType: "TeacherDirected",
            expiresAt: status.teacherDirectedCooldownExpiresAt
                ? new Date(status.teacherDirectedCooldownExpiresAt)
                : null,
            teacherKaid: status.teacherDirectedCooldownStartedBy?.kaid ?? null,
            teacherName:
                status.teacherDirectedCooldownStartedBy?.nickname ?? null,
        };
    }
    if (status.hasAntiCheatCooldown) {
        return {
            hasCooldown: true,
            cooldownType: "AntiCheat",
            expiresAt: status.lastAntiCheatCooldownExpiresAt
                ? new Date(status.lastAntiCheatCooldownExpiresAt)
                : null,
            teacherKaid: null,
            teacherName: null,
        };
    }
    return {
        hasCooldown: false,
        cooldownType: "None",
        expiresAt: null,
        teacherKaid: null,
        teacherName: null,
    };
}
